import { ThunkMiddleware } from '@reduxjs/toolkit';
import { JSONObject, bind } from 'immer-yjs';
import { isActionSynced } from 'redux-state-sync';
import { PortalUser } from 'vet-bones/utils/portal';

import { ProjectsAction } from 'src/actions/projects/actionCreators';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import { isProjectReadOnlySelector } from 'src/selectors/isProjectReadOnly';
import { collaborationSlice } from 'src/store/collaboration/collaboration.slice';
import { CollaborationActionMeta, ReduxState } from 'src/store/types';
import {
  hasProjectChanged,
  pruneDeletedProjectResources,
} from 'src/store/utils';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { queryClient } from 'src/utils/queryClient';

// VET-6754: for a read-only collaborator (a viewer, or an editor demoted
// because another editor holds the single write slot) every project-mutation
// action is blocked so no phantom local Redux/diagram edits diverge from the
// shared Y.Doc. This allowlist is intentionally narrow: the live-update channel
// and project create/fork/load/restore flows that spawn a new project rather
// than mutate the shared read-only one.
// Pure reads (REQUEST_PROJECT_MODEL_*) are NOT listed: their payload wraps the
// project id under payload.action.id rather than top-level payload.id, so the
// gate never sees a payloadId for them and passes them through automatically.
// A forgotten write action is blocked by default; a forgotten read surfaces as a
// QA-visible "can't view" bug, not silent corruption.
const READ_ONLY_PASS_THROUGH: ReadonlySet<ProjectsActionType> = new Set([
  // Critical: this is how a reader receives the writer's live edits.
  ProjectsActionType.RECEIVED_COLLABORATION_UPDATE,

  // Project create/import/load — each spawns a new project, not a mutation.
  ProjectsActionType.ADDED_PROJECT,
  ProjectsActionType.LOADED_PROJECT_FROM_CLOUD,
  ProjectsActionType.IMPORTED_PROJECT_FROM_FILE,
  ProjectsActionType.OPENED_MISSING_PROJECT,
  ProjectsActionType.EXPORTED_PROJECT,

  // Copy-to-local / migrate — fork into a new (local or cloud) project rather
  // than mutate the shared read-only one.
  ProjectsActionType.DUPLICATED_LOCAL_PROJECT,
  ProjectsActionType.DUPLICATED_CLOUD_BACKUP_TO_LOCAL_PROJECT,
  ProjectsActionType.MIGRATED_CLOUD_PROJECT_TO_LOCAL,
  ProjectsActionType.MIGRATED_LOCAL_PROJECT_TO_CLOUD,
  ProjectsActionType.MIGRATED_LOCAL_PROJECT_BACKUP_TO_CLOUD,
  ProjectsActionType.BACKED_UP_CLOUD_PROJECT_TO_LOCAL_PROJECT,
  ProjectsActionType.RESTORED_LOCAL_PROJECT,
  ProjectsActionType.RESTORED_CLOUD_PROJECT,
]);

// beforeunload handler to disconnect all collaboration WebSocket connections.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    collaborationProvider.disconnectAll();
  });
}

// Tracks the last time a RECEIVED_COLLABORATION_UPDATE was processed per project.
// Used to suppress the GoJS echo: when collaboration merges new data into GoJS,
// GoJS fires UPDATED_PROJECT_DIAGRAM back, which would write to Y.Doc and
// bounce between windows. We skip Y.Doc writes for a short window after a
// collaboration update to break this cycle.
const lastCollaborationUpdateByProject = new Map<string, number>();
const COLLABORATION_ECHO_WINDOW_MS = 500;

export const collaborationMiddleware: ThunkMiddleware<ReduxState> =
  (store) =>
  (next) =>
  async (action: ProjectsAction & { meta?: Record<string, unknown> }) => {
    // Drop cross-tab sync of diagram updates when collaboration is active
    if (
      isActionSynced(action) &&
      action.type === ProjectsActionType.UPDATED_PROJECT_DIAGRAM &&
      action.payload?.id &&
      collaborationProvider.isConnected(action.payload.id)
    ) {
      return undefined;
    }

    const portalUser = queryClient.getQueriesData({
      queryKey: 'usePortalUser',
    })?.[0]?.[1] as PortalUser;

    const isDesignerStorageEnabled = portalUser?.is_designer_storage_enabled;
    if (!isDesignerStorageEnabled) {
      return next(action);
    }

    const isInitialLoadOfCloudProject =
      action.type === ProjectsActionType.LOADED_PROJECT_FROM_CLOUD &&
      !store.getState().projects.projectsById[action.payload.project.id];

    const { payload } = action;
    const payloadId =
      payload && typeof payload === 'object' && 'id' in payload && payload.id
        ? payload.id
        : undefined;

    // VET-6754: central write gate. For a read-only project, block every
    // mutation except the pass-through allowlist. Checking the allowlist first
    // means reads / copy-to-local / RECEIVED_COLLABORATION_UPDATE never even
    // query read-only state. Returning here (no `next()`) prevents the local
    // Redux mutation and, because this middleware runs before
    // cloudProjectMiddleware, blocks the REST and Y.Doc writes downstream in one
    // place. The provider's per-connection flag is consulted first so the gate
    // fails closed during connect/reconnect (it defaults to read-only until the
    // server's `Authenticated` message), before Redux has been populated via
    // setCollaborationReadOnly; the selector is the fallback when there is no
    // active connection.
    if (
      payloadId &&
      !READ_ONLY_PASS_THROUGH.has(action.type) &&
      (collaborationProvider.isReadOnly(payloadId) ??
        isProjectReadOnlySelector(payloadId)(store.getState()))
    ) {
      return undefined;
    }

    const oldProject = payloadId
      ? store.getState().projects.projectsById[payloadId]
      : undefined;

    // When collaboration is connected for this project, the Y.Doc write below
    // is the source of truth for persistence. Tag the action so the downstream
    // cloudProjectMiddleware skips its legacy save instead of re-deriving this.
    // A single value drives both the tag and our own Y.Doc gate, so the two
    // middlewares can never disagree about which path persists the change.
    const collaborationActive = Boolean(
      payloadId && collaborationProvider.isConnected(payloadId)
    );

    // Merge into (not overwrite) the action's existing meta: RTK thunk actions
    // carry their own `meta` (arg / requestId / requestStatus) that downstream
    // reducers read, so clobbering it would break them.
    const collaborationMeta: CollaborationActionMeta = { collaborationActive };
    const actionResult = await next({
      ...action,
      meta: { ...action.meta, ...collaborationMeta },
    });

    // The collaboration WebSocket lifecycle (connect/disconnect + auth-listener
    // registration) is owned by useCollaborationConnection, scoped to the
    // project route — see src/utils/hooks/useCollaborationConnection.ts. On the
    // initial cloud-project load this middleware only forwards the action; it no
    // longer opens the connection here.
    if (isInitialLoadOfCloudProject) {
      return actionResult;
    }

    // The remaining handlers operate on a cloud project id.
    if (!payloadId) {
      return actionResult;
    }

    // isActionSynced(action) indicates the action came from another tab (which
    // handled its own collaboration side effects). RECEIVED_COLLABORATION_UPDATE
    // is already from a remote Y.Doc update; record its time so the GoJS echo
    // (UPDATED_PROJECT_DIAGRAM) it triggers is suppressed below.
    if (
      (oldProject && !oldProject.isCloudProject) ||
      (oldProject && oldProject.isRemoved) ||
      isActionSynced(action) ||
      action.type === ProjectsActionType.RECEIVED_COLLABORATION_UPDATE
    ) {
      if (action.type === ProjectsActionType.RECEIVED_COLLABORATION_UPDATE) {
        lastCollaborationUpdateByProject.set(payloadId, Date.now());
      }
      return actionResult;
    }

    if (action.type === ProjectsActionType.ARCHIVED_PROJECT) {
      // Disconnect from collaboration if active. The REST archive call lives in
      // cloudProjectMiddleware.
      collaborationProvider.disconnect(payloadId);
      store.dispatch(
        collaborationSlice.actions.clearedCollaborationProject({
          projectId: payloadId,
        })
      );
      return actionResult;
    }

    // A connection change persists to the cloud `connection_id` column
    // (handled by cloudProjectMiddleware), never to the Y.Doc.
    if (action.type === ProjectsActionType.UPDATED_PROJECT_CONNECTION_ID) {
      return actionResult;
    }

    const updatedProject = store.getState().projects.projectsById[payloadId];

    // If collaboration is active, write detected changes to the Y.Doc. The same
    // `collaborationActive` value was tagged onto the action, and
    // cloudProjectMiddleware skips its legacy save when it sees it — so exactly
    // one path persists the change.
    if (updatedProject?.isCloudProject && collaborationActive) {
      const prunedProject = pruneDeletedProjectResources(updatedProject);
      if (hasProjectChanged(oldProject, prunedProject)) {
        // Suppress GoJS echo: when a RECEIVED_COLLABORATION_UPDATE merges
        // remote data into Redux, GoJS reacts and fires UPDATED_PROJECT_DIAGRAM
        // back. Writing that echo to Y.Doc would bounce between windows.
        const lastCollabUpdate =
          lastCollaborationUpdateByProject.get(payloadId) ?? 0;
        if (Date.now() - lastCollabUpdate < COLLABORATION_ECHO_WINDOW_MS) {
          return actionResult;
        }
        const doc = collaborationProvider.getDoc(payloadId);
        if (doc) {
          const rootMap = doc.getMap('project');
          const binder = bind<JSONObject>(rootMap);
          // The first write into a brand-new (empty) doc populates the whole
          // project at once. Tag it `'init'` so the Y.UndoManager (which
          // tracks only `'local'`) ignores it — otherwise a single undo
          // would erase the entire project. Real edits use `'local'` and are
          // individually undoable (VET-6292).
          const origin = rootMap.size === 0 ? 'init' : 'local';
          doc.transact(() => {
            binder.update(() => {
              return { ...prunedProject } as unknown as JSONObject;
            });
          }, origin);
          binder.unbind();
        }
        // SCS handles persistence — no local save indicator needed
      }
    }

    return actionResult;
  };
